import {
    AddressCategory,
    LaneGroupTemperatureRange,
    LaneStatus,
    LeaseType,
    LocationType,
} from 'app/enums/label.enums'
import {
    RateCardsData,
} from 'app/requests/RateCard/RateCard.types'
import {
    DTOCardFormField, FieldInCard, NestedField,
} from 'app/types/form.types'
import {
    Country,
} from 'app/Apps/ContactManagement/Countries/Countries.types'
import {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'

import {
    AccountLane, AccountLaneProductTypes,
} from '../AccountLanes.types'
import getHandoverAlternateFields from '../AccountLaneForm/sections/handoverAlternate.fields'
import getCollectionAlternateFields from '../AccountLaneForm/sections/collectionAlternate.fields'
import getHandoverFields from '../AccountLaneForm/sections/handover.fields'
import getCollectionFields from '../AccountLaneForm/sections/collection.fields'

export type AccountLaneFormFields = AccountLane & AccountLaneProductTypes & {
    originPositioningFtl?: boolean,
    originPositioningLtl?: boolean,
    originTruckCapacity?: number,
    destinationCollectionLtl?: boolean,
}

const getStatusIsPricedFields = ({
    getAllowedValues,
    showFTL,
    showLTL,
    isDeliveryRoad,
    isCollection,
    handoverPointCountry,
    collectionDropoffPointCountry,
    exists,
    quoteInLink,
    quoteInNumber,
    disabled,
    requiredBillingCompany,
    showHandoverCollectionSection,
    logisticDetails,
    isAlternativeFlow,
}: {
    getAllowedValues: (val: Record<string, string>) => Record<string, string>,
    showFTL: boolean,
    showLTL: boolean,
    isDeliveryRoad: boolean,
    isCollection: boolean,
    handoverPointCountry?: Country | { id: number },
    collectionDropoffPointCountry?: Country | { id: number },
    showProductTypes?: boolean,
    showAwardedProductTypes?: boolean,
    exists: boolean,
    quoteInLink?: string,
    quoteInNumber?: string,
    disabled?: boolean,
    isAccountAdmin?: boolean,
    laneLeadPermission?: boolean,
    requiredBillingCompany?: boolean,
    tempRangeDisabled?: boolean,
    accountDisabled?: boolean,
    showHandoverCollectionSection: boolean,
    logisticDetails?: RateCardsData,
    isAlternativeFlow?: boolean
}): DTOCardFormField<keyof AccountLaneFormFields>[] => {
    const generalFields: FieldInCard<keyof AccountLaneFormFields>[] = [
        {
            id: 'column1',
            fields: [
                {
                    name: 'laneNumber',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.laneNumber',
                    disabled: true,
                },
                {
                    name: 'account',
                    componentName: 'AccountSelector',
                    labelKey: 'AccountLane.accountCompanyName',
                    required: true,
                    disabled: true,
                },
                ...exists ? [{
                    name: 'contractBasisAccount',
                    componentName: 'AccountSelector',
                    labelKey: 'Prebooking.pricingAccount',
                    disabled: true,
                }] as NestedField<keyof AccountLaneFormFields>[] : [],
                {
                    name: 'temperatureRange',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LaneGroupTemperatureRange),
                    labelKey: 'AccountLane.temperatureRange',
                    required: true,
                    useDropdown: true,
                    disabled: true,
                },
                {
                    name: 'leaseType',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LeaseType),
                    labelKey: 'AccountLane.leaseType',
                    required: true,
                    showInColumns: true,
                    disabled: true,
                },
                {
                    name: 'aQuoteId',
                    componentName: 'SkyNetLink',
                    link: quoteInLink,
                    text: quoteInNumber,
                    type: TypeLink.EXTERNAL,
                    size: SizeLink.S,
                    labelKey: 'AccountLaneForm.quotedIn',
                    disabled: true,
                },
            ],
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'laneStatus',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LaneStatus),
                    labelKey: 'LaneQuoteRequest.selectedLaneStatus',
                    disabled: true,
                },
                {
                    name: 'expPalletsYear',
                    required: true,
                    componentName: 'InputSingleline',
                    labelKey: 'LaneBasicView.expPalletsYear',
                },
                {
                    name: 'originAirport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType: LocationType.AIRPORT,
                    labelKey: 'AccountLaneForm.originAirport',
                    required: true,
                },
                {
                    name: 'destinationAirport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType: LocationType.AIRPORT,
                    labelKey: 'AccountLaneForm.destinationAirport',
                    required: true,
                },
                {
                    name: 'laneLead',
                    componentName: 'UserObjectSelector',
                    labelKey: 'AccountLane.laneLead',
                    required: true,
                },
                {
                    name: 'laneSupporter',
                    componentName: 'UserObjectSelector',
                    labelKey: 'AccountLane.laneSupporter',
                },
            ],
        },
        {
            id: 'column3',
            fields: [
                {
                    name: 'customerSite',
                    componentName: 'LocationObjectSelector',
                    labelKey: 'LaneUpdateNewDto.customerSite',
                },
                {
                    name: 'billingCompany',
                    componentName: 'BillingCompanyObjectSelector',
                    labelKey: 'LaneUpdateNewDto.billingCompany',
                    required: requiredBillingCompany,
                },
                {
                    name: 'customerExecution',
                    componentName: 'LocationObjectSelector',
                    labelKey: 'LaneQuoteRequest.selectedCustomerExecution',
                    filterLocationType: LocationType.ADDRESS,
                    filterAddressCategory: [
                        AddressCategory.CUSTOMER,
                        AddressCategory.CUSTOMER_QUOTATION,
                    ],
                },
                {
                    name: 'customerExecutionProvisional',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.customerExecutionProvisional',
                    disabled: true,
                },
                {
                    name: 'customerExecutionContacts',
                    componentName: 'MultipleContactObjectSelector',
                    labelKey: 'AccountLane.customerExecutionContacts',
                },
            ],
        },
        {
            id: 'column4',
            fields: [
                {
                    name: 'consigneeProvisional',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.consigneeProvisional',
                },
                {
                    name: 'laneComment',
                    componentName: 'LongText',
                    labelKey: 'AccountLane.laneComment',
                },
                {
                    name: 'createdOn',
                    componentName: 'DateTimeSelect',
                    disabled: true,
                    labelKey: 'Accounts.createdOn',
                },
                {
                    name: 'createdBy',
                    componentName: 'InputSingleline',
                    disabled: true,
                    labelKey: 'Accounts.createdBy',
                },
            ],
        },
    ]

    const handoverFn = isAlternativeFlow ? getHandoverAlternateFields : getHandoverFields
    const collectionFn = isAlternativeFlow ? getCollectionAlternateFields : getCollectionFields

    return [
        {
            id: 'general',
            title: 'General',
            className: 'general',
            fields: generalFields,
        },
        ...(showHandoverCollectionSection ? [
            {
                id: 'handover',
                title: 'Handover',
                className: 'handover',
                fields: handoverFn({
                    getAllowedValues,
                    disabled,
                    logisticDetails,
                    showFTL,
                    showLTL,
                    handoverPointCountry,
                    isDeliveryRoad,
                    isStatusPriced: true,
                }),
            },
            {
                id: 'collection',
                title: 'Collection',
                className: 'collection',
                fields: collectionFn({
                    disabled,
                    getAllowedValues,
                    isCollection,
                    collectionDropoffPointCountry,
                    logisticDetails,
                    isStatusPriced: true,
                }),
            },
        ] : []),
    ]
}

export default getStatusIsPricedFields
